import { computed, watchEffect, ref } from 'vue'
import { gql } from '@apollo/client/core'
import { useQuery, useMutation } from '/@/core/graphql'
import { type DailyPlay, type DesignerDailyPlay } from './types'
import { useRollbar } from '/@/core/rollbar'
import { vendorFragment } from '../products/fragments'

export interface DailyPlayUpdateCompletedInput {
  dailyPlayId: string
  isCompleted: boolean
}

export interface DailyPlayUpdateDismissedInput {
  dailyPlayId: string
  isDismissed: boolean
}

const getDesignerDailyPlayDataQuery = gql`
  query getDesignerDailyPlayData {
    user {
      id
      designer {
        id
        dailyPlays {
          id
          completedAt
          thing {
            id
            thingType
          }
        }
      }
    }
  }
`

export const dailyPlayUpdateFragment = gql`
  fragment DailyPlayUpdateFragment on DailyPlay {
    id
    designerInfo {
      id
      completedAt
      dismissedAt
    }
  }
`

export function useCurrentDailyPlayData() {
  const dailyPlayResponse = useQuery<{ currentDailyPlay?: DailyPlay }>(gql`
    ${vendorFragment}
    query getCurrentDailyPlayData {
      currentDailyPlay {
        id
        prompt
        name
        url
        type
        designerInfo {
          id
          dismissedAt
          completedAt
          thing {
            id
            thingType
          }
        }
        vendor {
          ...VendorFields
          logoUrl
        }
      }
    }
  `)
  const designerResponse = useQuery<{
    user?: {
      designer?: {
        dailyPlays: DesignerDailyPlay[]
      }
    }
  }>(getDesignerDailyPlayDataQuery)

  const dailyPlayData = computed(
    () => dailyPlayResponse.result.value?.currentDailyPlay,
  )
  const designerDailyPlays = computed(
    () => designerResponse.result.value?.user?.designer?.dailyPlays ?? [],
  )

  const rollbar = useRollbar()
  watchEffect(() => {
    if (dailyPlayResponse.error.value) {
      rollbar.error(dailyPlayResponse.error.value)
    }
    if (designerResponse.error.value) {
      rollbar.error(designerResponse.error.value)
    }
  })

  const dailyPlay = computed(() => {
    return {
      current: dailyPlayData.value,
      completed: designerDailyPlays.value,
    }
  })

  const loading = computed(
    () => dailyPlayResponse.loading.value || designerResponse.loading.value,
  )

  return { dailyPlay, loading }
}

export function useDailyPlayData(dailyPlayId?: string) {
  if (!dailyPlayId) return { dailyPlay: ref(null), loading: ref(false) }

  const { result, loading } = useQuery<{ dailyPlay?: DailyPlay }>(
    gql`
      query useDailyPlayData($id: String!) {
        dailyPlay(id: $id) {
          id
          prompt
          name
          url
          type
          designerInfo {
            id
            dismissedAt
            completedAt
            thing {
              id
              thingType
            }
          }
        }
      }
    `,
    {
      id: dailyPlayId,
    },
  )

  const dailyPlay = computed(() => result.value?.dailyPlay)

  return { dailyPlay, loading }
}

export function useDailyPlayApi() {
  const { mutate: updateCompleted } = useMutation(
    gql`
      ${dailyPlayUpdateFragment}
      mutation updateCompleted($input: DailyPlayUpdateCompletedInput!) {
        dailyPlay {
          updateCompleted(input: $input) {
            record {
              ...DailyPlayUpdateFragment
            }
          }
        }
      }
    `,
    {
      refetchQueries: [{ query: getDesignerDailyPlayDataQuery }],
    },
  )

  const { mutate: updateDismissed } = useMutation(gql`
    ${dailyPlayUpdateFragment}
    mutation updateDismissed($input: DailyPlayUpdateDismissedInput!) {
      dailyPlay {
        updateDismissed(input: $input) {
          record {
            ...DailyPlayUpdateFragment
          }
        }
      }
    }
  `)

  return {
    async updateCompleted(input: DailyPlayUpdateCompletedInput) {
      await updateCompleted({ input })
    },
    async updateDismissed(input: DailyPlayUpdateDismissedInput) {
      await updateDismissed({ input })
    },
  }
}
